import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../constants/api-endpoints';
import {
  NotificationHistoryApiItem,
  NotificationHistoryPage,
  NotificationHistoryApiResponse,
  NotificationHistoryRequest,
  NotificationHistoryViewModel,
} from '../models/notification-history.model';

@Injectable({
  providedIn: 'root',
})
export class NotificationHistoryService {
  private readonly api = inject(ApiService);

  getNotifications(
    login: string,
    pageSize: number = 50,
    startRowNum: number = 0
  ): Observable<NotificationHistoryApiResponse> {
    const payload = new NotificationHistoryRequest(login, {
      pageSize,
      startRowNum,
    });
    return this.api.post<typeof payload, NotificationHistoryApiResponse>(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_NOTIFICATION_HISTORY,
      payload
    );
  }

  getUnreadNotifications(
    login: string,
    pageSize: number = 5,
    startRowNum: number = 0
  ): Observable<NotificationHistoryApiResponse> {
    const payload = new NotificationHistoryRequest(login, {
      dcMethod: 'GETUNREADLIST',
      pageSize,
      startRowNum,
    });

    return this.api.post<typeof payload, NotificationHistoryApiResponse>(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_NOTIFICATION_HISTORY,
      payload
    );
  }

  getUnreadCount(login: string): Observable<number> {
    const payload = new NotificationHistoryRequest(login, {
      dcMethod: 'UNREADCOUNT',
    });

    return this.api.post<typeof payload, NotificationHistoryApiResponse>(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_NOTIFICATION_HISTORY,
      payload
    ).pipe(
      map((response) => Number(response?.UnReadCount ?? 0) || 0)
    );
  }

  getNotificationViewModels(
    login: string,
    lang: string = 'en',
    pageSize: number = 50,
    startRowNum: number = 0
  ): Observable<NotificationHistoryPage> {
    return this.getNotifications(login, pageSize, startRowNum).pipe(
      map((response) => ({
        notifications: this.mapNotifications(response, lang),
        totalRecords: Number(response?.TotalRecords ?? 0) || 0,
      }))
    );
  }

  getUnreadNotificationViewModels(
    login: string,
    lang: string = 'en',
    pageSize: number = 5,
    startRowNum: number = 0
  ): Observable<NotificationHistoryPage> {
    return this.getUnreadNotifications(login, pageSize, startRowNum).pipe(
      map((response) => ({
        notifications: this.mapNotifications(response, lang),
        totalRecords: Number(response?.UnReadCount ?? 0) || 0,
      }))
    );
  }

  markAsRead(
    login: string,
    notificationId: string,
    created: string = ''
  ): Observable<NotificationHistoryApiResponse> {
    const payload = new NotificationHistoryRequest(login, {
      dcMethod: 'UPDATE',
      id: notificationId,
      created,
    });

    return this.api.postWithoutRetry<typeof payload, NotificationHistoryApiResponse>(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_NOTIFICATION_HISTORY,
      payload
    );
  }

  private mapNotifications(
    response: NotificationHistoryApiResponse,
    lang: string
  ): NotificationHistoryViewModel[] {
    const items = this.normalizeNotifications(response);

    return items
      .map((item, index) => this.toViewModel(item, index, lang))
      .sort((a, b) => {
        const aTime = a.createdOnDate?.getTime() ?? Number.MIN_SAFE_INTEGER;
        const bTime = b.createdOnDate?.getTime() ?? Number.MIN_SAFE_INTEGER;
        return bTime - aTime;
      });
  }

  private normalizeNotifications(
    response: NotificationHistoryApiResponse
  ): NotificationHistoryApiItem[] {
    const items = response?.SiebelMessage?.['DC Notify History BC'];
    if (!items) {
      return [];
    }

    return Array.isArray(items) ? items : [items];
  }

  private toViewModel(
    item: NotificationHistoryApiItem,
    index: number,
    lang: string
  ): NotificationHistoryViewModel {
    const created = item.Created ?? '';
    const createdOnDate = this.parseDate(created);
    const title =
      lang === 'ar'
        ? item['DC Title AR'] || item['DC Title EN'] || ''
        : item['DC Title EN'] || item['DC Title AR'] || '';
    const subject =
      lang === 'ar'
        ? item['DC Subject AR'] || item['DC Subject EN'] || ''
        : item['DC Subject EN'] || item['DC Subject AR'] || '';
    const serviceParts = [item['SR Type'], item['SR Sub Type']].filter(
      (part): part is string => !!part && part.trim().length > 0
    );
    const membershipName =
      lang === 'ar'
        ? item['DC Member Name Ara'] || item['DC Member Name Eng'] || ''
        : item['DC Member Name Eng'] || item['DC Member Name Ara'] || '';
    const status = item['SR Status'] ?? '';
    const downloadUrl = this.isValidUrl(item['DC Download URL'])
      ? item['DC Download URL']!
      : null;

    return {
      id: item.Id || item['SR Number'] || `notification-${index}`,
      heading: title,
      body: subject,
      service: serviceParts.join(' / '),
      membershipName,
      memberNumber: item['DC Member Number'] ?? '',
      srNumber: item['SR Number'] ?? '',
      status,
      created,
      createdOnDate,
      icon: this.resolveIcon(status, downloadUrl),
      highlight: (item['DC Read Flag'] ?? '').toUpperCase() !== 'Y',
      downloadUrl,
      raw: item,
      CSN: item['DC Member Number'] ?? ''
    };
  }

  private resolveIcon(
    status: string,
    downloadUrl: string | null
  ): 'info' | 'success' | 'warning' | 'error' {
    const normalizedStatus = status.toLowerCase();

    if (
      normalizedStatus.includes('reject') ||
      normalizedStatus.includes('cancel') ||
      normalizedStatus.includes('error')
    ) {
      return 'error';
    }

    if (
      normalizedStatus.includes('await') ||
      normalizedStatus.includes('pending') ||
      normalizedStatus.includes('clarification')
    ) {
      return 'warning';
    }

    if (
      normalizedStatus.includes('paid') ||
      normalizedStatus.includes('closed') ||
      normalizedStatus.includes('complete') ||
      !!downloadUrl
    ) {
      return 'success';
    }

    return 'info';
  }

  private parseDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const direct = new Date(value);
    if (!Number.isNaN(direct.getTime())) {
      return direct;
    }

    const match = value.match(
      /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (!match) {
      return null;
    }

    const [, month, day, year, hour = '0', minute = '0', second = '0'] = match;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private isValidUrl(value?: string | null): boolean {
    if (!value) {
      return false;
    }

    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
